import * as React from 'react';
import Downshift from 'downshift';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import {withStyles,
  WithStyles,
  StyleRules,
  ClassNameMap
} from '@mui/styles';
import { IconButton } from '@mui/material';
import { Theme } from '@mui/styles';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import { APIError } from '../../common/lib/types';
import { defer, debounce } from 'lodash';
// import until from 'async/until';
import Chip from '@mui/material/Chip';
import zIndex from '@mui/styles/zIndex';

let lastId = 0;

function uniqueID(prefix = 'id') {
  lastId++;
  return `${prefix}${lastId}`;
}

interface CancellablePromise<T> extends Promise<T> {
  cancel?: () => void;
}

type QueryPromise = CancellablePromise<any>;

const styles = (theme: Theme): StyleRules => ({
  container: {
    flexGrow: 1,
    position: 'relative'
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(3),
    left: 0,
    right: 0
  },
  chip: {
    margin: `${theme.spacing(.1)}px ${theme.spacing(.25)}px`,
  },
  suggestion: {
    display: 'block'
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none'
  },
  textField: {
    width: '100%',
    marginTop: 15
  }
});

export interface AutoSuggestQuery {
  value: string;
}

interface Props extends React.HTMLProps<HTMLDivElement> {
  onSuggestionSelected: (event: any, data: any) => void;
  fetchSuggestions?: (query: AutoSuggestQuery) => QueryPromise;
  staticOptions?: Array<any>;
  placeholder: string;
  value?: string;
  minQueryChars?: number;
  disabled?: boolean;
  error?: boolean;
  onToggleChip?: () => void;
  helperText?: string;
  menuWidth?: number | string;
  autoSelectOptions?: boolean;
  name: string;
  preventBusyTab?: boolean;
  maxHeight?: number;
  displayClearAdornment?: boolean;
  emitClear?: boolean;
  required?: boolean;
  // indicates if the input should be shown in materialUI chips
  useChips?: boolean;
  chips?: any;
  suggestionRenderer?: any;
}

type State = {
  value: string;
  inputValue: string;
  selectedSuggestion?: object;
  suggestionRootVal?: string;
  rootSuggestions: Array<any>;
  suggestions: Array<any>;
  isOpen: boolean;
  error?: APIError;
  isDirty: boolean;
  searching: boolean;
};

interface CancellablePromise<T> extends Promise<T> {
  cancel?: () => void;
}

class AutoSuggest extends React.Component<
  Props & WithStyles<typeof styles>,
  State
> {
  openTransaction?: Promise<any>;
  openTransactionCount: number;
  currentlyHightlightedIndex?: number;
  popperNode: any;
  anchorEl: any;
  constructor(props: Props & WithStyles<keyof typeof styles>) {
    super(props, {});
    this.openTransactionCount = 0;
    this.anchorEl = React.createRef();
    this.state = {
      value: props.value || '',
      inputValue: props.value || '',
      rootSuggestions: props.staticOptions || [],
      suggestions: [],
      isOpen: false,
      isDirty: false,
      searching: false
    };
  }

  // TODO:  See if we can rework this to use componentDidUpdate (https://fb.me/react-derived-state)
  UNSAFE_componentWillReceiveProps(newProps: any) {
    if (typeof (newProps.value) == "object") {
      console.warn("setting inputValue to object", newProps.value)
    }
    
    this.setState({ value: newProps.value, inputValue: newProps.value });
  }

  // This is called when the user selects an item by cliking it in the list
  // or using the cursor to select the item and then hitting [Enter]
  // The
  onDownshiftChange = (selectedItem: any) => {

    
    this.setState({
      selectedSuggestion: selectedItem,
      value: selectedItem.value,
      inputValue: selectedItem.label,
      isOpen: false
    });
    this.props.onSuggestionSelected(null, {
      suggestionValue: selectedItem.label
    });
  };

  onDownshiftSelect = (selectedItem: any) => {
    if (selectedItem != null && this.state.suggestions.length > 0){
      this.currentlyHightlightedIndex = this.state.suggestions.findIndex(
        (obj: any) => {
          return obj['value'] == selectedItem.value;
        }
      );
    }
    // We should not get into this situation but just in case.
    if(this.currentlyHightlightedIndex == -1){
    	this.currentlyHightlightedIndex = 0
    }
    

    if (selectedItem) {
      
      this.setState({
        value: selectedItem.value,
        inputValue: selectedItem.label,
        isOpen: false
      });
    
      this.props.onSuggestionSelected(null, { suggestionValue: selectedItem });
    }
  };

  // This is called as the user is typing into the input box.
  // We will use this to maintain state and to initial the fetching of suggestions as necessary
  onInputChange = (onChange: Function) => (evt: any) => {
    const targetVal = evt.target.value;
    const { minQueryChars, autoSelectOptions, staticOptions, suggestionRenderer } = this.props;
    const { suggestionRootVal, rootSuggestions, inputValue } = this.state;
    
    if (!targetVal) {

    }
    // We are going to keep track of the input value in state, so that we can manage the functionality directly
    if (targetVal == '' || targetVal == null) {
      this.props.onSuggestionSelected(null, { suggestionValue: { value: null } });
      // this.setState({searching: false, rootSuggestions:[], inputValue: targetVal });

      this.setState({
        rootSuggestions: staticOptions || [],
        // inputValue: targetVal,
        searching: false,
        suggestions: [],
        inputValue: '',
        value: '',
        isDirty: false,
        isOpen: false
      });
    } else {
      // Resolve premature search showing: this.setState({searching:true, isDirty: true, inputValue: targetVal });
      //
      if (typeof (targetVal) == "object") {
        console.warn("setting inputValue to object", targetVal)
      }
      
      this.setState({ isDirty: true, inputValue: targetVal });
    }

    // We either have not yet searched or the search is not relevant
    if (!minQueryChars || targetVal.length >= minQueryChars) {
      // Something is changing so reset the selected index
      this.currentlyHightlightedIndex = 0;

      // Next, lets see if the search term is still relevant, given the query when the search was performed
      if (
        rootSuggestions.length > 0 &&
        (staticOptions || suggestionRootVal == '')
      ) {
        // If we have already gotten suggestions and the are still relevant
        // just filter those instead of making a needless call to the server
        const suggestions = rootSuggestions.filter((s: any) => {
          if(suggestionRenderer) {
            return true 
          } else {
            return (
              s.label.toLowerCase().indexOf(targetVal.toLowerCase()) > -1 ||
              s.value.toLowerCase().indexOf(targetVal.toLowerCase()) > -1
            );
          }
        });

        if (suggestions.length > 0) {
          let val = targetVal;
          if (suggestions[0].value) {
            // val = suggestions[0].value;
            
            if (autoSelectOptions && (suggestions || []).length == 1) {
              this.props.onSuggestionSelected(null, {
                suggestionValue: suggestions[0]
              });
            }
          }

          this.setState({
            searching: false,
            value: val,
            inputValue: targetVal,
            suggestions: suggestions
          });
        } else {
          this.setState({
            searching: false,
            rootSuggestions: staticOptions || [],
            suggestions: suggestions
          });
        }
      } else {
        this.setState(
          {
            searching: true
          },
          () => {
            if (this.openTransaction) {
              console.warn(
                'There is a transaction still returning, but we are issuing a new transaction'
              );
            }
            var self = this;
            // this.openTransactionCount++;
            if (this.props.fetchSuggestions) {
            debounce(()=>{
              this.openTransactionCount++
              this.openTransaction = this.props
                .fetchSuggestions({ value: targetVal })
                .then((items: Array<any>) => {
                  this.openTransactionCount--;
                  delete this.openTransaction;
                  if (items.length > 0) {
                    let val = targetVal;
                    if (items[0].value) {
                      val = items[0].value;
                      if (autoSelectOptions && (items || []).length == 1) {
                        this.props.onSuggestionSelected(null, {
                          suggestionValue: items[0]
                        });
                      }
                    }
                    // TODO: Discuss how we handle when s.value is an object
                    const suggestions = items.filter((s: any) => {
                      if(suggestionRenderer) {
                        return true 
                      } else {
                        return (
                          s.label.toLowerCase().indexOf(targetVal.toLowerCase()) >
                          -1 ||
                          (typeof s.value == 'string' &&
                            s.value
                              .toLowerCase()
                              .indexOf(targetVal.toLowerCase()) > -1)
                        );
                      }
                    });


                    var iv = targetVal
                    if (typeof (iv) == "object") {
                      console.warn("setting inputValue to object", iv)

                    }

                    if (suggestions.length == 1){
                      val = suggestions[0].value
                    }
                    
                    this.setState({
                      // value: val,
                      // inputValue: iv,
                      suggestions: suggestions,
                      rootSuggestions: items,
                      suggestionRootVal: targetVal,
                      searching: false //self.openTransactionCount > 0,
                    });
                  } else {
                    this.openTransactionCount--;
                    this.setState(
                      {
                        searching:
                          self.openTransactionCount > 0 &&
                          (targetVal || '').length() > 0,
                        suggestions: items,
                        rootSuggestions: items,
                        suggestionRootVal: targetVal
                      },
                      () => {
                        this.openTransactionCount = 0
                        this.props.onSuggestionSelected(null, {});
                      }
                    );
                  }
                })
                .catch((err: APIError) => {
                  this.openTransactionCount--;
                  delete this.openTransaction;
                  this.setState({
                    error: err,
                    searching: false
                  });
                });
              },100)()
            }
          
          }
        );
      }
    } else {
      this.setState({ searching: false });
    }

    // Now call the internal onChange event.  This will trigger the opening of the list
    if (onChange) {
      onChange(evt);
    }
  
  };

  handleClear = (event: any) => {
    this.setState(
      {
        searching: false,
        inputValue: '',
        value: '',
        isDirty: false,
        selectedSuggestion: {},
        rootSuggestions: this.props.staticOptions || [],
        suggestions: this.props.staticOptions || [],
      },
      () => {
        if (this.props.emitClear) {
          this.props.onSuggestionSelected(null, {
            suggestionValue: { value: null }
          });
        }
        this.popperNode.focus();
      }
    );
  };
  onInputFocus = (evt: any) => { };

  onInputBlur = (evt: any) => {
    this.setState({
      searching: false
    });
  };

  onInputKeyDown = (onKeyDown: any) => (evt: any) => {
    // If there is an item that the user has highlighted when they tab out of the field, select that
    const { useChips } = this.props;
    
    if (evt.keyCode == 9 || evt.keyCode == 13) {
      if (this.props.preventBusyTab && this.state.searching) {
        evt.preventDefault();
        return;
      } else {
        var self = this;
        const selectedItem =
          self.state.suggestions[self.currentlyHightlightedIndex || 0];


        if (
          (evt.keyCode == 9 || evt.keyCode == 13) &&
          (self.state.inputValue == null ||
            self.state.inputValue == '' ||
            (self.state.isOpen == false && (self.state.inputValue)))
        ) {


          onKeyDown(evt);

          return;
        }
        evt.preventDefault();

        // IE11 doesn't support spread operator. If that changes we may be able to re-introduce async.until.
        setTimeout(() => {
          if (self.state.suggestions[self.currentlyHightlightedIndex || 0]) {

            const selectedItem =
              self.state.suggestions[self.currentlyHightlightedIndex || 0];

            self.props.onSuggestionSelected(null, {
              suggestionValue: selectedItem
            });


            self.setState({
              value: selectedItem.value,
              inputValue: useChips?"": selectedItem.label,
              isOpen: false,
              isDirty: false
            });
          }
          self.setState({
            searching: false,
            isOpen: false
          });
        }, 100)

        // until(
        //   function (cb) {
        //     // As part of the MUI Update, async/until changed
        //     // return !self.state.searching;
        //     cb(null, !self.state.searching)
        //   },
        //   function (cb) {
        //     setTimeout(function () {
        //       cb();
        //     }, 100);
        //   },
        //   function () {
        //     if (self.state.suggestions[self.currentlyHightlightedIndex || 0]) {

        //       const selectedItem =
        //         self.state.suggestions[self.currentlyHightlightedIndex || 0];

        //       self.props.onSuggestionSelected(null, {
        //         suggestionValue: selectedItem
        //       });

        //       self.setState({
        //         value: selectedItem.value,
        //         inputValue: selectedItem.value,
        //         isOpen: false,
        //         isDirty: false
        //       });
        //     }
        //     self.setState({
        //       searching: false,
        //       isOpen: false
        //     });
        //   }
        // );
      }
    } else {
      if (evt.keyCode == 40 || evt.keyCode == 38) {
        evt.stopPropagation()
        evt.preventDefault();
      }
      
      onKeyDown(evt);
      
    }
  
  };

  onMenuItemClicked = (selectedItem: any) => () => {
    var self = this;
    self.currentlyHightlightedIndex = self.state.suggestions.findIndex(
      (obj: any) => {
        return obj['value'] == selectedItem.value;
      }
    );

    // 2021-04-23 MV: When clicking on a selection option, if the user tabbed out of the field, there is nowhere to tab from. 
    // This will place the cursor back in the source element to continue tab sequence
    // Place focus back on the source elment that caused the dropdown to fix tabbing issue
    if (this.popperNode) {
      this.popperNode.focus()
    }

    self.props.onSuggestionSelected(null, { suggestionValue: selectedItem });

    self.setState(
      {
        // searching: false,
        // selectedSuggestion: selectedItem,
        value: selectedItem.value,
        inputValue: selectedItem.value,
        isOpen: false,
        isDirty: false
      }
    );
    self.setState({
      searching: false,
      isOpen: false
    });
  };

  downshiftStateReducer = (state: object, changes: object): object => {
    // capture any internal changes that we need to here
    if (changes.hasOwnProperty('highlightedIndex')) {


      this.currentlyHightlightedIndex = changes['highlightedIndex'] as number;
      if (
        this.state.suggestions[this.currentlyHightlightedIndex] &&
        this.state.suggestions[this.currentlyHightlightedIndex].value
      ) {


        
        this.setState({
          searching: false,
          // value: this.state.suggestions[this.currentlyHightlightedIndex].value,
          // inputValue: this.state.suggestions[this.currentlyHightlightedIndex].label,
          isDirty: false
        });
      }
    }
    if (changes.hasOwnProperty('isOpen')) {
      this.setState({ isOpen: changes['isOpen'] });
    }
    if (changes.hasOwnProperty('selectedItem')) {
      this.props.onSuggestionSelected(null, {
        suggestionValue: changes['selectedItem']
      });
      if (changes['selectedItem'] != null) {
        this.setState({
          searching: false,
          value: changes['selectedItem'].value,
          inputValue: changes['selectedItem'].label,
          isDirty: false,
          isOpen: false
        });
      } else {
        this.setState({
          searching: false,
          isDirty: false,
          isOpen: false
        });
      }
    }
    return changes;
  };

  renderInput = (inputProps: any, uID: string) => {
    const {
      classes,
      disabled,
      error,
      helperText,
      name,
      required,
      disabled
    }: {
      classes: ClassNameMap;
      disabled?: boolean;
      error?: boolean;
      helperText?: string;
      name?: string;
      required?: boolean;
      disabled?: boolean;
    } = this.props;
    const {
      autoFocus,
      value,
      onChange,
      ref,
      onBlur,
      onKeyDown,
      ...other
    } = inputProps;
    const { searching } = this.state;
    // Since we are trying to mutate inputValue, chaning to to a var vs a const to remove warning
    let { inputValue } = this.state

    if (inputValue && typeof (inputValue) == "object") {
      if (inputValue["label"]) {
        console.warn("invalid input value (object), using label attribute: ", inputValue["label"])
        inputValue = inputValue["label"]
      } else {
        console.error("invalid input value, Text Field cannot accept objects without a label attribute, setting input value to ''")
        inputValue = ""
      }
    }
    return (
      <TextField
        name={name}
        onBlur={this.onBlur}
        className={classes.textField}
        disabled={disabled}
        autoFocus={autoFocus}
        error={error}
        value={inputValue}
        inputRef={(node: any) => {
          this.popperNode = node;
        }}
        required={required}
        helperText={helperText}
        inputProps={{
          autoComplete: 'off'
        }}
        InputProps={{
          id: uID,
          disabled: disabled,
          style: {
            width: "calc(100%-50px)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          },
          endAdornment: (
            <InputAdornment position="end" style={{ marginLeft: 0 }}>
              <i
                style={{ cursor: 'pointer' }}
                onClick={this.handleClear}
                className={
                  searching
                    ? 'fa fa-sync fa-spin'
                    : this.props.displayClearAdornment
                      ? (inputValue + '').length > 0
                        ? 'fa fa-times-square fa-xs'
                        : 'fa'
                      : 'fa'
                }
              />
            </InputAdornment>
          ),
          onChange: this.onInputChange(onChange),
          onFocus: this.onInputFocus,
          onKeyDown: this.onInputKeyDown(onKeyDown),
          ...other
        }}
      />
    );
  };

  renderSuggestion = (
    suggestion: any,
    isSelected: boolean,
    isHighlighted: boolean,
    opts: any,
    idx: number
  ) => {
    const { itemProps } = opts;
    const { suggestionRenderer } = this.props;
     

    var rendered:any = (
      <div>{suggestion.menuContent || suggestion.label}</div>
    )

    if(suggestionRenderer){
      const S = suggestionRenderer
      rendered = (
        <S data={suggestion} />
      )
    }

    return (
      <MenuItem
        // autoFocus={idx == 0}
        {...itemProps}
        id={opts.id} //This fixes one issue but makes the list choppy when using arrow keys
        className={'auto-suggest-value-' + suggestion.value}
        key={suggestion.label + idx}
        selected={isHighlighted}
        component="div"
        style={{
          fontWeight: isSelected ? 500 : 400
        }}
        onKeyUp={() => {

          this.currentlyHightlightedIndex++
          this.setState({})

        }}
        onClick={this.onMenuItemClicked(suggestion)}
        onTouchStart={this.onMenuItemClicked(suggestion)}
        >
          
        {rendered}
      </MenuItem>
    );
  };

  onBlur = (event: any) => {
    // Dont leave the field if a search is in progress otherwise the value will be cleared

    defer(() => {
      
      if (
        ((this.state.rootSuggestions || []).length == 0 &&
          this.state.isDirty) ||
        this.state.inputValue == ''
      ) {

        this.setState(
          {
            inputValue: '',
            value: '',
            isOpen: false
          },
          () => {
            this.props.onSuggestionSelected(null, { suggestionValue: {} });
          }
        );
      } else if (
        this.state.inputValue != this.props.value ||
        this.state.searching
      ) {
        if (!this.state.searching) {
          var keepOpen: boolean = document.activeElement && document.activeElement.getAttribute("role") == "menuitem"
          keepOpen = false;
          if (typeof (this.props.value) == "object") {
            console.warn("setting inputValue to object", this.props.value)
          }


          this.setState(
            {
              inputValue: this.props.value || '',
              value: this.props.value || '',
              isOpen: keepOpen,
              rootSuggestions: this.props.staticOptions || []
              // suggestions:[],
            },
            () => {
              if (this.state.searching) {
                // this.props.onSuggestionSelected(null, { suggestionValue: {} });
              }
            }
          );
        }
      } else {
        this.setState({
          rootSuggestions: this.props.staticOptions || []
        })
      }
    });
  };
  handleDelete = item => () => {
    // const selectedSuggestion = this.state.selectedSuggestion;
    // selectedSuggestion.splice(selectedSuggestion.indexOf(item), 1);

    this.setState({ selectedSuggestion: {} });
  };

  render() {
    const { placeholder, name, classes, useChips, chips, suggestionRenderer } = this.props;
    const { isOpen } = this.state;

    return (
      <Downshift
        isOpen={isOpen}
        defaultHighlightedIndex={0}
        stateReducer={this.downshiftStateReducer}
        onChange={this.onDownshiftChange}
        onSelect={this.onDownshiftSelect}
        itemToString={(item: any) => {
          return item ? item.value : '';
        }}>
        {(downshift: any) => {
          const {
            getInputProps,
            getItemProps,
            isOpen,
            inputValue,
            selectedItem,
            highlightedIndex
          } = downshift;


          let inputProps = getInputProps({
            placeholder: placeholder
          });
          // If the correct flags are in place, this uses the chips provided by parent components in the input.
          let closeChipButton = (
            <InputAdornment position="end">
              <IconButton tabIndex={-1}
                onClick={this.props.onToggleChip}
                size={"small"}>

                {this.state.searching && <i className={"fa fa-sync fa-spin"} />}
                {!this.state.searching && <i className={"fal fa-list"} />}
              </IconButton>
            </InputAdornment>
          )
          if (useChips && chips) {
            inputProps = getInputProps({
              startAdornment:
                chips,
              placeholder: placeholder,
              endAdornment: closeChipButton
            });
          } else if (useChips) {
            inputProps = getInputProps({
              startAdornment:
                chips,
              endAdornment: closeChipButton,
              placeholder: placeholder
            });
          }

          const uID = uniqueID(this.props.name);
          const inputControl = this.renderInput(inputProps, uID);
          var menuWidth = this.props.menuWidth;
          var transformString = `translate3d(0px, 0px, 0px)`
          if (this.popperNode) {
            const rect = this.popperNode.getBoundingClientRect()
            transformString = `translate3d(${rect.left + window.pageXOffset}px, ${rect.bottom + window.pageYOffset}px, 0px)`
          }
          return (
            <div className={'auto-suggest ' + name}>
              {inputControl}
              <Popper
                style={{
                  zIndex: 1400,
                  position: "absolute",
                  transform: transformString,
                  top: 0,
                  left: 0,
                  willChange: "transform"
                }}
                open={isOpen}
                // modifiers={{ flip: { enabled: true }, applyStyle: { enabled: false } }}
                anchorEl={this.popperNode}
                placement="bottom-start"
                transition>
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={350}>
                    <Paper
                      className={'auto-suggest-menu ' + name}
                      style={{
                        width: menuWidth,
                        position: 'relative',
                        maxHeight: this.props.maxHeight || 400,
                        overflow: 'auto'
                      }}
                      square>
                      {this.state.suggestions.filter((i: any) => {
                          if(suggestionRenderer){
                          // if we are using a custom suggestion renderer, then we do not want to filter it at all here
                          // Since not all of the values will be part of the label, which is the default behavior.
                            return true 
                          } else {
                            return (
                              !inputValue ||
                              (i.label || '')
                                .toLowerCase()
                                .indexOf(inputValue.toLowerCase()) > -1 ||
                              (String(i.value) || '')
                                .toLowerCase()
                                .indexOf(inputValue.toLowerCase()) > -1
                            );
                          }
                        }) .map((item: any, index: number) => {
                          const isSelected = item == selectedItem;
                          const isHighlighted = index == highlightedIndex;
                          return this.renderSuggestion(
                            item,
                            isSelected,
                            isHighlighted,
                            getItemProps({ item, index }),
                            index
                          );
                        })}
                    </Paper>
                  </Fade>
                )}
              </Popper>
            </div>
          );
        }}
      </Downshift>
    );
  }
}

export default withStyles(styles)(AutoSuggest);
